// Tiny Excel formula evaluator for the Classic workbook's output sheets (refs, & = <> < > <= >= + - * /, IF, IFS, CHAR, TODAY)
const tokenize = (src) => {
  const re = /\s*(?:("(?:[^"]|"")*")|('(?:[^']|'')+'|[A-Za-z_][\w.]*)!(\$?[A-Z]+\$?\d+(?::\$?[A-Z]+\$?\d+)?)|(\$?[A-Z]+\$?\d+(?::\$?[A-Z]+\$?\d+)?)(?![\w(])|(\d+(?:\.\d+)?)|([A-Za-z_][\w.]*)\s*\(|(<>|<=|>=|[-+*/&=<>(),]))/gy;
  const out = [];
  let m;
  re.lastIndex = 0;
  while (re.lastIndex < src.length && (m = re.exec(src))) {
    if (m[1]) out.push({ t: "str", v: m[1].slice(1, -1).replace(/""/g, '"') });
    else if (m[2]) out.push({ t: "ref", sheet: m[2].replace(/^'|'$/g, "").replace(/''/g, "'"), v: m[3] });
    else if (m[4]) out.push({ t: "ref", sheet: null, v: m[4] });
    else if (m[5]) out.push({ t: "num", v: parseFloat(m[5]) });
    else if (m[6]) out.push({ t: "fn", v: m[6].replace(/^_xlfn\./i, "").toUpperCase() });
    else if (m[7]) out.push({ t: "op", v: m[7] });
    if (!m[0].length) break;
  }
  return out;
};

const toNum = (v) => (v === "" || v == null ? 0 : typeof v === "number" ? v : typeof v === "boolean" ? +v : Number.isFinite(+v) && String(v).trim() !== "" ? +v : NaN);
const cmp = (a, b) => {
  const na = typeof a === "number" || (a !== "" && a != null && Number.isFinite(+a));
  const nb = typeof b === "number" || (b !== "" && b != null && Number.isFinite(+b));
  if ((a === "" || a == null) && nb) a = 0;
  if ((b === "" || b == null) && na) b = 0;
  if (na && nb) return toNum(a) - toNum(b);
  if (na !== nb && a !== "" && b !== "") return na ? -1 : 1; // Excel: numbers sort before text
  return String(a ?? "").toLowerCase().localeCompare(String(b ?? "").toLowerCase());
};

// getRef(sheet|null, "B26") → raw cell value (string/number/"")
export const evaluateFormula = (formula, getRef) => {
  const toks = tokenize(formula.replace(/^=/, ""));
  let i = 0;
  const peek = () => toks[i];
  const isOp = (v) => peek()?.t === "op" && peek().v === v;
  const primary = () => {
    const tk = toks[i++];
    if (!tk) return "";
    if (tk.t === "str" || tk.t === "num") return tk.v;
    if (tk.t === "ref") { const v = getRef(tk.sheet, tk.v.replace(/\$/g, "").split(":")[0]); return v === "" || v == null ? null : v; }
    if (tk.t === "op" && tk.v === "(") { const v = expr(); if (isOp(")")) i++; return v; }
    if (tk.t === "op" && tk.v === "-") return -toNum(primary());
    if (tk.t === "op" && tk.v === "+") return toNum(primary());
    if (tk.t === "fn") {
      const args = [];
      if (!isOp(")")) { do { const start = i; skipArg(); args.push({ start }); } while (isOp(",") && ++i); }
      if (isOp(")")) i++;
      const after = i;
      const val = (k) => { i = args[k].start; const v = expr(); return v; };
      let res = "";
      if (tk.v === "IF") { const c = val(0); res = truthy(c) ? (args[1] ? val(1) : true) : (args[2] ? val(2) : false); }
      else if (tk.v === "IFS") { res = "#N/A"; for (let k = 0; k + 1 < args.length; k += 2) if (truthy(val(k))) { res = val(k + 1); break; } }
      else if (tk.v === "CHAR") res = String.fromCharCode(toNum(val(0)));
      else if (tk.v === "TODAY") { const d = new Date(); res = `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`; }
      else res = "#NAME?";
      i = after;
      return res;
    }
    return "";
  };
  // advance past one argument without evaluating (balanced parentheses)
  const skipArg = () => { let depth = 0; while (i < toks.length) { const tk = toks[i]; if (tk.t === "fn") depth++; else if (tk.t === "op" && tk.v === "(") depth++; else if (tk.t === "op" && tk.v === ")") { if (depth === 0) return; depth--; } else if (tk.t === "op" && tk.v === "," && depth === 0) return; i++; } };
  const truthy = (v) => (typeof v === "boolean" ? v : typeof v === "number" ? v !== 0 : String(v).toUpperCase() === "TRUE");
  const mul = () => { let v = primary(); while (isOp("*") || isOp("/")) { const op = toks[i++].v; const r = primary(); v = op === "*" ? toNum(v) * toNum(r) : toNum(v) / toNum(r); } return v; };
  const add = () => { let v = mul(); while (isOp("+") || isOp("-")) { const op = toks[i++].v; const r = mul(); v = op === "+" ? toNum(v) + toNum(r) : toNum(v) - toNum(r); } return v; };
  const cat = () => { let v = add(); while (isOp("&")) { i++; v = `${fmt(v)}${fmt(add())}`; } return v; };
  const expr = () => {
    let v = cat();
    while (["=", "<>", "<", ">", "<=", ">="].some(isOp)) {
      const op = toks[i++].v; const c = cmp(v, cat());
      v = { "=": c === 0, "<>": c !== 0, "<": c < 0, ">": c > 0, "<=": c <= 0, ">=": c >= 0 }[op];
    }
    return v;
  };
  try { const r = expr(); return r === null ? 0 : r; } catch { return "#VALUE!"; }
};

export const fmt = (v) => (typeof v === "boolean" ? (v ? "TRUE" : "FALSE") : typeof v === "number" ? (Number.isNaN(v) ? "#VALUE!" : String(+v.toFixed(10))) : v ?? "");
